import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  MenuItem,
  CircularProgress,
  TextField,
} from "@mui/material";
import NextIcon from "../../../assets/image/Nexticon.svg";
import { CustomInput } from "../../../components/CustomInput";
import { successAlert } from "../../../utils/success-alert";
import { errorAlert } from "../../../utils/error-alert";
import { CustomMenuItem } from "../../../components/CustomMenuItem";
import { CustomSelect } from "../../../components/CustomSelect";
import moment from "moment";
import "moment-timezone";
import { attachmentErrorAlert } from "../../../utils/error-attachmentErrorAlert";
import { CustomAutocomplete } from "../../../components/CustomAutocomplete";
import { getVendors } from "../../../services/vendor/vendor";
import { getAgentList } from "../../../services/agent/agent";
import {
  getOfflineTransaction,
  saveOfflineTransaction,
  uploadOfflineTransactionScanCopy,
} from "../../../services/account/offline-transaction";
import { useNavigate, useParams } from "react-router-dom";

const timezoneOptions = [
  { value: "UTC", label: "UTC" },
  { value: "Asia/Dhaka", label: "Bangladesh (GMT+6)" },
  { value: "Asia/Dubai", label: "Dubai (GMT+4)" },
  { value: "Asia/Kathmandu", label: "Nepal (GMT+5:45)" },
  { value: "Asia/Kolkata", label: "India (GMT+5:30)" },
  { value: "Asia/Singapore", label: "Singapore (GMT+8)" },
  { value: "Asia/Kuala_Lumpur", label: "Malaysia (GMT+8)" },
  { value: "Asia/Ho_Chi_Minh", label: "Vietnam (GMT+7)" },
  { value: "Asia/Phnom_Penh", label: "Cambodia (GMT+7)" },
  { value: "America/New_York", label: "New York (GMT-5)" },
  { value: "Europe/London", label: "London (GMT+0)" },
];

const serviceCategories = {
  ALL: ["ALL"],
  FLIGHT: ["ISSUE", "REISSUE", "SSR", "ADDON", "ADM", "OTHER", "ALL"],
  VISA: ["APPLY", "ADDON", "OTHER", "PENALTY", "ALL"],
  TRANSPORT: ["BOOK", "EXCHANGE", "ADDON", "OTHER", "PENALTY", "ALL"],
  TOUR: ["BOOK", "EXCHANGE", "ADDON", "OTHER", "PENALTY", "ALL"],
  RAIL: ["BOOK", "EXCHANGE", "ADDON", "OTHER", "PENALTY", "ALL"],
  UMRAH: ["BOOK", "EXCHANGE", "ADDON", "OTHER", "PENALTY", "ALL"],
  HOTEL: ["BOOK", "EXCHANGE", "ADDON", "OTHER", "PENALTY", "ALL"],
  OTHERS: ["BOOK", "EXCHANGE", "ADDON", "OTHER", "PENALTY", "ALL"],
};

const CreateOfflineService = () => {
  const params = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [b2bUsers, setB2bUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState("Asia/Dhaka");

  const [formData, setFormData] = useState({
    createdDateTime: moment().tz(selectedTimezone).toISOString(),
  });

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { payload: vendors },
        } = await getVendors();

        const {
          data: { payload: agents },
        } = await getAgentList();

        setVendors(vendors.data);
        setB2bUsers(agents.data);

        if (params.id === "new") {
          return;
        }

        const {
          data: { payload: newData },
        } = await getOfflineTransaction(params.id);

        console.log(newData);

        setFormData(newData);
      } catch (error) {
        if (error?.response?.status === 403) {
          errorAlert("You have no permission to access this page");
        } else {
          console.error("An error occurred while fetching data:", error);
          errorAlert("An unexpected error occurred. Please try again later.");
        }
      }
    })();
  }, []);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      createdDateTime: moment().tz(selectedTimezone).toISOString(),
    }));
  }, [selectedTimezone]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    const newData = { ...formData };
    newData[name] = value;

    if (name === "service") {
      setAvailableCategories(serviceCategories[value] || []);
    }

    setFormData(newData);
  };

  const handleScanCopyUpload = async (trxId) => {
    try {
      if (file) {
        await uploadOfflineTransactionScanCopy(trxId, file);
      }
    } catch (error) {
      throw new Error("Error uploading");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    setTimeout(async () => {
      try {
        const result = await saveOfflineTransaction(formData);

        if (result.data?.success) {
          if (file) {
            if (!file) {
              attachmentErrorAlert("No file selected for upload.");
              setLoading(false);
              return;
            }

            const allowedTypes = [
              "image/png",
              "image/jpeg",
              "image/jpg",
              "application/pdf",
            ];
            if (!allowedTypes.includes(file.type)) {
              attachmentErrorAlert(
                "Invalid file type. Only images (JPG, PNG, JPEG) or PDF are allowed."
              );
              setLoading(false);
              return;
            }

            if (file.size > 1024 * 1024) {
              attachmentErrorAlert(
                "File size exceeds 1MB. Please upload a smaller file."
              );
              setLoading(false);
              return;
            }

            await handleScanCopyUpload(result.data.payload?.id);
          }

          successAlert("Trx Added Successfully");
          navigate("/dashboard/transactions/offline-services");
        } else {
          errorAlert("Operation failed. Please try again.");
        }
      } catch (error) {
        if (error.response) {
          const errorMessage =
            error.response.data?.message ||
            "An error occurred. Please try again.";
          errorAlert(errorMessage);
        } else {
          errorAlert(
            "Network error. Please check your connection and try again."
          );
        }
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  const resetForm = () => {
    setFormData({});
    setFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleTimezoneChange = (event) => {
    setSelectedTimezone(event.target.value);
  };

  const formatDatetime = (datetime) => {
    return moment(datetime).tz(selectedTimezone).format("YYYY-MM-DDTHH:mm");
  };

  return (
    <Box sx={{ backgroundColor: "#FFFFFF", borderRadius: "8px", padding: 2 }}>
      <form onSubmit={handleSubmit}>
        <Box mt={2}>
          <Typography
            sx={{
              padding: "5px 0px",
              fontSize: "15px",
              fontWeight: 400,
              color: "#A56EB4",
              fontFamily: "Outfit",
            }}
          >
            Create Offline Transaction
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6E6996",
                fontFamily: "Outfit",
              }}
            >
              <span style={{ color: "red" }}>*</span> Select Timezone
            </Typography>
            <CustomSelect
              required
              value={selectedTimezone}
              onChange={handleTimezoneChange}
            >
              {timezoneOptions.map((timezone) => (
                <MenuItem key={timezone.value} value={timezone.value}>
                  {timezone.label}
                </MenuItem>
              ))}
            </CustomSelect>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6E6996",
                fontFamily: "Outfit",
              }}
            >
              <span style={{ color: "red" }}>*</span> Transaction Date
            </Typography>
            <CustomInput
              required
              name="createdDateTime"
              placeholder="Enter Created Date Time"
              type="datetime-local"
              value={
                formData.createdDateTime
                  ? formatDatetime(formData.createdDateTime)
                  : ""
              }
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  createdDateTime: moment(e.target.value)
                    .tz(selectedTimezone)
                    .toISOString(),
                }))
              }
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6E6996",
                fontFamily: "Outfit",
              }}
            >
              <span style={{ color: "red" }}>*</span> Service
            </Typography>
            <CustomSelect
              required
              displayEmpty
              name="service"
              onChange={handleChange}
              value={formData.service || ""}
              renderValue={(selected) => selected || "Select Service Type"}
            >
              <CustomMenuItem value="">Select Service</CustomMenuItem>
              <CustomMenuItem value="ALL">ALL</CustomMenuItem>
              <CustomMenuItem value="FLIGHT">FLIGHT</CustomMenuItem>
              <CustomMenuItem value="VISA">VISA</CustomMenuItem>
              <CustomMenuItem value="TRANSPORT">TRANSPORT</CustomMenuItem>
              <CustomMenuItem value="TOUR">TOUR</CustomMenuItem>
              <CustomMenuItem value="RAIL">RAIL</CustomMenuItem>
              <CustomMenuItem value="UMRAH">UMRAH</CustomMenuItem>
              <CustomMenuItem value="HOTEL">HOTEL</CustomMenuItem>
              <CustomMenuItem value="OTHERS">OTHERS</CustomMenuItem>
            </CustomSelect>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6E6996",
                fontFamily: "Outfit",
              }}
            >
              <span style={{ color: "red" }}>*</span> Service Category
            </Typography>
            <CustomSelect
              required
              name="serviceCategory"
              value={formData.serviceCategory || ""}
              onChange={handleChange}
              displayEmpty
              renderValue={(selected) => selected || "Select Service Category"}
            >
              {availableCategories.length > 0 ? (
                availableCategories.map((category) => (
                  <CustomMenuItem key={category} value={category}>
                    {category}
                  </CustomMenuItem>
                ))
              ) : (
                <CustomMenuItem disabled>
                  No Categories Available
                </CustomMenuItem>
              )}
            </CustomSelect>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6E6996",
                fontFamily: "Outfit",
              }}
            >
              Vendor
            </Typography>
            <CustomAutocomplete
              options={vendors?.filter((head) => !!head.vendorName)}
              getOptionLabel={(option) =>
                `${option.identificationShortCode || ""} - ${
                  option.vendorName || ""
                }`
              }
              renderOption={(props, option, { selected }) => (
                <CustomMenuItem {...props} selected={selected}>
                  {`${option.identificationShortCode || ""} - ${
                    option.vendorName || ""
                  }`}
                </CustomMenuItem>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search Vendor"
                  variant="outlined"
                  size="small"
                />
              )}
              value={
                vendors.find((head) => head.id === formData.vendorId) || null
              }
              onChange={(_, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  vendorId: newValue?.id || "",
                }));
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6E6996",
                fontFamily: "Outfit",
              }}
            >
              Vendor Payable
            </Typography>
            <CustomInput
              name="vendorPayable"
              placeholder="Enter Vendor Payable"
              type="number"
              value={formData.vendorPayable || ""}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6E6996",
                fontFamily: "Outfit",
              }}
            >
              Agent
            </Typography>
            <CustomAutocomplete
              options={b2bUsers?.filter((party) => !!party.companyName)}
              getOptionLabel={(option) =>
                `${option.identificationShortCode || ""} - ${
                  option.companyName || ""
                }`
              }
              renderOption={(props, option, { selected }) => (
                <CustomMenuItem {...props} selected={selected}>
                  {`${option.identificationShortCode || ""} - ${
                    option.companyName || ""
                  }`}
                </CustomMenuItem>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search Agent"
                  variant="outlined"
                  size="small"
                />
              )}
              value={
                b2bUsers.find((party) => party.id == formData.b2bUserId) || null
              }
              onChange={(_, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  b2bUserId: newValue?.id || "",
                }));
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6E6996",
                fontFamily: "Outfit",
              }}
            >
              User Payable
            </Typography>
            <CustomInput
              name="userPayable"
              placeholder="Enter User Payable"
              type="number"
              value={formData.userPayable || ""}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6E6996",
                fontFamily: "Outfit",
              }}
            >
              <span style={{ color: "red" }}>*</span> Remarks
            </Typography>
            <CustomInput
              required
              name="remarks"
              placeholder="Enter Remarks"
              value={formData.remarks || ""}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6E6996",
                fontFamily: "Outfit",
              }}
            >
              Transaction Receipt{" "}
              <span
                style={{
                  color: "#FF9FAE",
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                (No larger than "1 MB")
              </span>
            </Typography>
            <Box
              sx={{
                border: "2px dashed #B4B4CD",
                borderRadius: "8px",
                p: 1,
                display: "flex",
                alignItems: "center",
                color: "#B4B4CD",
                cursor: "pointer",
              }}
            >
              <input
                type="file"
                name="transactionReceipt"
                accept="image/png, image/jpeg, image/jpg, application/pdf"
                ref={fileInputRef} // Attach ref here
                onChange={(e) => {
                  const selectedFile = e.target.files[0];

                  if (selectedFile) {
                    const allowedTypes = [
                      "image/png",
                      "image/jpeg",
                      "image/jpg",
                      "application/pdf",
                    ];
                    if (!allowedTypes.includes(selectedFile.type)) {
                      attachmentErrorAlert(
                        "Invalid file type. Only images (JPG, PNG, JPEG) or PDF are allowed."
                      );
                      return;
                    }

                    if (selectedFile.size > 1024 * 1024) {
                      attachmentErrorAlert(
                        "File size exceeds 1MB. Please upload a smaller file."
                      );
                      return;
                    }

                    setFile(selectedFile); // Ensure the file is properly set
                  }
                }}
              />
            </Box>
          </Grid>
        </Grid>

        <Box
          mt={3}
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            gap: "16px",
          }}
        >
          <Button
            sx={{
              textTransform: "none",
              fontSize: "13px",
              fontWeight: 500,
              backgroundColor: "#B4B4CD",
              color: "#FFFFFF",
              borderRadius: "4px",
              width: "60px",
              fontFamily: "Outfit",
              "&:hover": {
                backgroundColor: "#B4B4CD",
              },
              "&:focus": {
                backgroundColor: "#B4B4CD",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            sx={{
              textTransform: "none",
              fontSize: "13px",
              fontWeight: 500,
              backgroundColor: "#6E0A82",
              color: "#FFFFFF",
              borderRadius: "4px",
              width: "110px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "Outfit",
              cursor: loading ? "not-allowed" : "pointer",
              "&:hover": {
                backgroundColor: "#6E0A82",
              },
              "&:focus": {
                backgroundColor: "#6E0A82",
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={15} sx={{ mr: 1, color: "#FFFFFF" }} />
                <Typography
                  sx={{
                    color: "#FFFFFF",
                    fontSize: "13px",
                    fontFamily: "Outfit",
                  }}
                >
                  Submit
                </Typography>
              </>
            ) : (
              <>
                <Typography
                  sx={{
                    color: "#FFFFFF",
                    fontSize: "13px",
                    fontFamily: "Outfit",
                  }}
                >
                  Submit
                </Typography>
                <Box
                  component="img"
                  src={NextIcon}
                  alt="Next Icon"
                  sx={{
                    ml: 1,
                    width: "14px",
                    height: "14px",
                  }}
                />
              </>
            )}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default CreateOfflineService;
